// views/Financeiro/lib/janelaFaturamentoLista.ts
//
// Lógica pura da visualização em LISTA da Janela de Faturamento/Cobrança.
//
// A visão em cards (Semana/Dia) distribui as cobranças em colunas com teto diário
// (40 por analista) e empurra o excedente para os dias seguintes — boa para o
// trabalho do dia, ruim para enxergar o todo. A lista mostra TODAS as cobranças dos
// meses selecionados, sem teto, com filtro e ordenação por coluna.
//
// Os itens que chegam aqui já passaram pelo `displayItems` do PendentesCalculoModal
// (janela real/prevista, data crítica, urgência e responsável estável resolvidos).
// Este arquivo só classifica, filtra e ordena — não recalcula janela nenhuma, para
// que a lista e os cards nunca discordem sobre a data limite de um item.

import { diffInDays } from '../../../src/lib/janelaCobranca.ts';
import type { PrevisaoFatura } from '../../../src/lib/previsaoFaturaEquatorial.ts';

export type Urgencia = 'critical' | 'warning' | 'normal' | 'sem_previsao';

/** Etapa da cobrança no fluxo, da falta de fatura até o fim. */
export type SituacaoCobranca =
  | 'sem_fatura'
  | 'a_calcular'
  | 'calculado'
  | 'emitido'
  | 'enviado'
  | 'pago'
  | 'nao_precisa_calcular'
  | 'cancelado';

export type StatusRobo = 'na_fila' | 'falhou';

export interface CobrancaJanela {
  uc?: string | number | null;
  outro_numero_uc?: string | number | null;
  nome?: string | null;
  expectedMonth: string;
  rateioNome?: string | null;
  /** `rateios_credito_itens.status` do rateio que gerou a cobrança ('Ativa', '1° Ciclo'...). */
  statusRateio?: string | null;
  responsavel_id?: string | null;
  responsavel_nome?: string | null;
  fatura?: {
    id?: number | string;
    status_fatura?: string | null;
    pular_calculo_em?: string | null;
    data_cobranca_agendada?: string | null;
    uc_codigo?: string | null;
    mes_referencia?: string | null;
  } | null;
  hasFatura?: boolean;
  isCalculada?: boolean;
  status?: Urgencia | string;
  inicioJanelaIso?: Date | null;
  fimJanelaIso?: Date | null;
  criticoJanelaIso?: Date | null;
  targetDateIso?: Date | null;
  /** Leitura + dias úteis: chegada, apresentação e limite de atraso (previsaoFaturaEquatorial). */
  previsaoFatura?: PrevisaoFatura | null;
  /** Sem fatura depois do prazo de chegada (9º dia útil; 3º na impressa na hora). */
  atrasadaEquatorial?: boolean;
  /** Limite de emissão até 2 dias depois da chegada prevista da fatura. */
  prazoCurto?: boolean;
  hasManualReschedule?: boolean;
  ucData?: { nome_cliente?: string | null; titular_fatura?: string | null } | null;
}

export const SITUACOES: { valor: SituacaoCobranca; rotulo: string; pendente: boolean }[] = [
  { valor: 'sem_fatura', rotulo: 'Sem fatura', pendente: true },
  { valor: 'a_calcular', rotulo: 'A calcular', pendente: true },
  { valor: 'calculado', rotulo: 'Boleto calculado', pendente: false },
  { valor: 'emitido', rotulo: 'Emitido / a enviar', pendente: false },
  { valor: 'enviado', rotulo: 'Cobrança enviada', pendente: false },
  { valor: 'pago', rotulo: 'Pago', pendente: false },
  { valor: 'nao_precisa_calcular', rotulo: 'Não precisa calcular', pendente: false },
  { valor: 'cancelado', rotulo: 'Cancelado / excluído', pendente: false },
];

export const URGENCIAS: { valor: Urgencia; rotulo: string }[] = [
  { valor: 'critical', rotulo: 'Crítico' },
  { valor: 'warning', rotulo: 'Atenção' },
  { valor: 'normal', rotulo: 'No prazo' },
  { valor: 'sem_previsao', rotulo: 'Sem histórico' },
];

export const rotuloSituacao = (s: SituacaoCobranca): string =>
  SITUACOES.find((x) => x.valor === s)?.rotulo ?? s;

/** Status da UC no rateio que originou a cobrança. */
export type StatusRateio =
  | 'ativa'
  | 'ativa_sem_rateio'
  | 'primeiro_ciclo'
  | 'segundo_ciclo'
  | 'cancelada'
  | 'reativacao'
  | 'pausada'
  | 'troca_endereco'
  | 'outro';

const STATUS_RATEIO: Record<string, { codigo: StatusRateio; rotulo: string }> = {
  ativa: { codigo: 'ativa', rotulo: 'Ativa' },
  // Não colapsar em "Ativa": para a operação, "Ativa" quer dizer que a UC está
  // recebendo crédito — e a sem rateio não está.
  'ativa-s/-rateio': { codigo: 'ativa_sem_rateio', rotulo: 'Ativa s/ rateio' },
  '1 ciclo': { codigo: 'primeiro_ciclo', rotulo: '1º ciclo' },
  '2 ciclo': { codigo: 'segundo_ciclo', rotulo: '2º ciclo' },
  cancelada: { codigo: 'cancelada', rotulo: 'Cancelada' },
  reativacao: { codigo: 'reativacao', rotulo: 'Reativação' },
  pausada: { codigo: 'pausada', rotulo: 'Pausada' },
  'troca de endereco': { codigo: 'troca_endereco', rotulo: 'Troca de endereço' },
};

/** `null` quando o rateio não trouxe status. Valor desconhecido passa com o texto original. */
export const classificarStatusRateio = (
  valor: string | null | undefined,
): { codigo: StatusRateio; rotulo: string } | null => {
  const original = String(valor ?? '').trim();
  if (!original) return null;
  // O upload grava "1° Ciclo" (grau); aceita também "1º" (ordinal) e sem acento.
  const chave = original
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[°º]/g, '')
    .replace(/\s+/g, ' ');
  return STATUS_RATEIO[chave] ?? { codigo: 'outro', rotulo: original };
};

/**
 * Situação da cobrança. "Não precisa calcular" vence o status da fatura: uma
 * fatura pulada segue o fluxo até "Cobrança enviada" (é o caso da maioria em
 * produção), mas para quem fatura o que importa é que ela não foi calculada.
 * Excluído/Cancelado vencem tudo — a linha saiu do fluxo.
 */
export const situacaoDaCobranca = (item: CobrancaJanela): SituacaoCobranca => {
  const f = item.fatura;
  const status = f?.status_fatura ?? '';

  if (status === 'Excluído' || status === 'Cancelado' || status === 'Cancelada') return 'cancelado';
  if (!item.isCalculada) {
    // "Aguardando Upload" é placeholder (reagendamento/robô), não fatura real.
    return item.hasFatura ? 'a_calcular' : 'sem_fatura';
  }
  if (f?.pular_calculo_em) return 'nao_precisa_calcular';
  if (status === 'Com boleto calculado') return 'calculado';
  if (status === 'Com boleto emitido / A enviar') return 'emitido';
  if (status === 'Cobrança enviada') return 'enviado';
  if (status === 'Pago' || status === 'Liquidado') return 'pago';
  return 'enviado';
};

export const ehPendente = (item: CobrancaJanela): boolean => !item.isCalculada;

/** Urgência só faz sentido enquanto a cobrança está pendente. */
export const urgenciaDaCobranca = (item: CobrancaJanela): Urgencia | null => {
  if (!ehPendente(item)) return null;
  const s = item.status;
  return s === 'critical' || s === 'warning' || s === 'sem_previsao' ? s : 'normal';
};

/** Mesma chave `uc:mes` que o modal usa no mapa da fila do robô. */
export const chaveFilaRobo = (item: CobrancaJanela): string => {
  const uc = item.hasFatura
    ? item.fatura?.uc_codigo || item.outro_numero_uc || item.uc
    : item.outro_numero_uc || item.uc;
  const mes = item.hasFatura ? item.fatura?.mes_referencia || item.expectedMonth : item.expectedMonth;
  return `${uc ?? ''}:${mes}`;
};

/**
 * O robô só importa enquanto falta a fatura. Com fatura ativa no mês, a gravação
 * dele é barrada como duplicata (trigger prevent_duplicate_active_fatura) — então
 * falha antiga ou tarefa na fila não dizem mais nada sobre a cobrança.
 */
export const statusRoboDaCobranca = (
  item: CobrancaJanela,
  fila: Map<string, { status?: string }>,
): StatusRobo | null => {
  if (!ehPendente(item) || item.hasFatura) return null;
  const entrada = fila.get(chaveFilaRobo(item));
  if (entrada?.status === 'pendente' || entrada?.status === 'processando') return 'na_fila';
  if (entrada?.status === 'falhou') return 'falhou';
  return null;
};

/** Por que a busca do robô terminou sem fatura — quase nunca é defeito do robô. */
export type MotivoRobo =
  | 'nao_disponivel'
  | 'bordero_pendente'
  | 'dados_incorretos'
  | 'site_instavel'
  | 'ja_cadastrada'
  | 'outro';

export const ROTULO_MOTIVO_ROBO: Record<MotivoRobo, string> = {
  nao_disponivel: 'Fatura não disponível',
  bordero_pendente: 'Borderô pendente',
  dados_incorretos: 'Dados do titular incorretos',
  site_instavel: 'Site da Equatorial instável',
  ja_cadastrada: 'Fatura já cadastrada',
  outro: 'Busca não concluída',
};

/** Classifica o `error_details` que o robô grava na fila. */
export const motivoDoRobo = (mensagem: string | null | undefined): MotivoRobo => {
  const m = String(mensagem ?? '').toLowerCase();
  // Antes de "não disponível": a mensagem do borderô também contém a expressão.
  if (m.includes('borderô')) return 'bordero_pendente';
  if (m.includes('não disponível') || m.includes('não encontrada em segunda via')) return 'nao_disponivel';
  if (m.includes('duplicata')) return 'ja_cadastrada';
  // Antes de "login": "Falha no login (A data de nascimento...)" é cadastro, não site.
  if (m.includes('dados de titular incorretos') || m.includes('dados incorretos') || m.includes('data de nascimento do titular')) {
    return 'dados_incorretos';
  }
  if (m.includes('login') || m.includes('timeout')) return 'site_instavel';
  return 'outro';
};

/** 'dd/mm/aaaa' (fila do robô) ou ISO (cadastro da UC) → 'aaaa-mm-dd'; inválido → ''. */
const dataParaIso = (valor: unknown): string => {
  const s = String(valor ?? '').trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : '';
};

/**
 * Depois de "dados do titular incorretos", retentar só faz sentido se o cadastro
 * mudou em relação ao que a Equatorial recusou (a fila guarda os dados usados na
 * tentativa) — senão o robô repete o mesmo login recusado.
 */
export const dadosTitularMudaram = (
  tentativa: { cpf_cnpj?: string | null; data_nascimento?: string | null } | null | undefined,
  cadastro: { cpf_cnpj_titular?: string | null; data_nascimento_titular?: string | null } | null | undefined,
): boolean => {
  const docAtual = soDigitos(cadastro?.cpf_cnpj_titular);
  if (!docAtual) return false;
  const semZerosEsquerda = (v: string) => v.replace(/^0+/, '');
  if (semZerosEsquerda(docAtual) !== semZerosEsquerda(soDigitos(tentativa?.cpf_cnpj))) return true;
  // CNPJ entra na Equatorial sem data de nascimento.
  if (docAtual.length !== 11) return false;
  return dataParaIso(cadastro?.data_nascimento_titular) !== dataParaIso(tentativa?.data_nascimento);
};

// ── Datas ────────────────────────────────────────────────────────────────────

/** YYYY-MM-DD no fuso LOCAL (toISOString daria o dia UTC). */
export const chaveData = (d: Date | null | undefined): string => {
  if (!d || isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export type CampoData = 'limite' | 'inicio' | 'fim' | 'prevista';

export const CAMPOS_DATA: { valor: CampoData; rotulo: string }[] = [
  { valor: 'limite', rotulo: 'Data limite' },
  { valor: 'inicio', rotulo: 'Início da janela' },
  { valor: 'fim', rotulo: 'Fim da janela' },
  { valor: 'prevista', rotulo: 'Data prevista' },
];

export const dataDoCampo = (item: CobrancaJanela, campo: CampoData): Date | null => {
  const d =
    campo === 'limite' ? item.criticoJanelaIso :
    campo === 'inicio' ? item.inicioJanelaIso :
    campo === 'fim' ? item.fimJanelaIso :
    item.targetDateIso;
  return d && !isNaN(d.getTime()) ? d : null;
};

/** Dias até a data limite (negativo = já passou). `null` sem data limite. */
export const diasParaLimite = (item: CobrancaJanela, hoje: Date): number | null => {
  const limite = dataDoCampo(item, 'limite');
  if (!limite) return null;
  return diffInDays(chaveData(limite), chaveData(hoje));
};

// ── Mês de referência ────────────────────────────────────────────────────────

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** 'SET/2026' → 202609. Formato desconhecido → 0 (vai para o início). */
export const numeroMesReferencia = (mes: string | null | undefined): number => {
  const [m, a] = String(mes || '').toUpperCase().split('/');
  const ano = parseInt(a, 10);
  let idx = MESES.indexOf(m);
  if (idx === -1) idx = parseInt(m, 10) - 1;
  if (isNaN(ano) || isNaN(idx) || idx < 0 || idx > 11) return 0;
  return ano * 100 + idx + 1;
};

/** Ordem cronológica (ordenar a string põe AGO/2026 antes de JUL/2026). */
export const ordenarMeses = (meses: string[]): string[] =>
  Array.from(new Set(meses.filter(Boolean))).sort((a, b) => numeroMesReferencia(a) - numeroMesReferencia(b));

/**
 * Meses marcados ao abrir a lista: o mais recente e todo mês anterior que ainda
 * tem cobrança pendente. Abrir só no mais recente escondia justamente as
 * pendências mais atrasadas — as mais críticas — e a lista divergia dos cards.
 */
export const mesesIniciaisDaLista = (itens: CobrancaJanela[]): string[] => {
  const meses = ordenarMeses(itens.map((i) => i.expectedMonth));
  if (!meses.length) return [];
  const maisRecente = meses[meses.length - 1];
  const comPendencia = new Set(itens.filter(ehPendente).map((i) => i.expectedMonth));
  return meses.filter((m) => m === maisRecente || comPendencia.has(m));
};

// ── Filtros ──────────────────────────────────────────────────────────────────

export interface FiltrosLista {
  busca: string;
  /** '' = todos */
  responsavel: string;
  /** vazio = todos */
  meses: string[];
  situacoes: SituacaoCobranca[];
  urgencias: Urgencia[];
  /** '' = todos */
  rateio: string;
  campoData: CampoData;
  /** YYYY-MM-DD, '' = sem limite */
  dataDe: string;
  dataAte: string;
  /** '' = todos */
  robo: '' | StatusRobo;
}

export const FILTROS_VAZIOS: FiltrosLista = {
  busca: '',
  responsavel: '',
  meses: [],
  situacoes: [],
  urgencias: [],
  rateio: '',
  campoData: 'limite',
  dataDe: '',
  dataAte: '',
  robo: '',
};

const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');

export const correspondeBusca = (item: CobrancaJanela, busca: string): boolean => {
  const termo = busca.trim().toLowerCase();
  if (!termo) return true;
  const textos = [item.nome, item.ucData?.nome_cliente, item.ucData?.titular_fatura];
  if (textos.some((t) => String(t || '').toLowerCase().includes(termo))) return true;
  const digitos = soDigitos(termo);
  if (!digitos) return false;
  return [item.uc, item.outro_numero_uc].some((uc) => soDigitos(uc).includes(digitos));
};

export const filtrarCobrancas = <T extends CobrancaJanela>(
  itens: T[],
  filtros: FiltrosLista,
  fila: Map<string, { status?: string }> = new Map(),
): T[] =>
  itens.filter((item) => {
    if (filtros.meses.length && !filtros.meses.includes(item.expectedMonth)) return false;
    if (filtros.responsavel && item.responsavel_id !== filtros.responsavel) return false;
    if (filtros.rateio && (item.rateioNome || '') !== filtros.rateio) return false;
    if (filtros.situacoes.length && !filtros.situacoes.includes(situacaoDaCobranca(item))) return false;
    if (filtros.urgencias.length) {
      const u = urgenciaDaCobranca(item);
      if (!u || !filtros.urgencias.includes(u)) return false;
    }
    if (filtros.robo && statusRoboDaCobranca(item, fila) !== filtros.robo) return false;
    if (filtros.dataDe || filtros.dataAte) {
      const chave = chaveData(dataDoCampo(item, filtros.campoData));
      if (!chave) return false;
      if (filtros.dataDe && chave < filtros.dataDe) return false;
      if (filtros.dataAte && chave > filtros.dataAte) return false;
    }
    return correspondeBusca(item, filtros.busca);
  });

// ── Ordenação ────────────────────────────────────────────────────────────────

export type CampoOrdem =
  | 'prioridade'
  | 'uc'
  | 'cliente'
  | 'mes'
  | 'responsavel'
  | 'situacao'
  | 'limite'
  | 'inicio'
  | 'fim'
  | 'prevista';

export interface Ordem {
  campo: CampoOrdem;
  direcao: 'asc' | 'desc';
}

export const ORDEM_PADRAO: Ordem = { campo: 'prioridade', direcao: 'asc' };

const PESO_URGENCIA: Record<Urgencia, number> = { critical: 0, warning: 1, sem_previsao: 1, normal: 2 };
const ORDEM_SITUACAO = new Map(SITUACOES.map((s, i) => [s.valor, i]));

const compararTexto = (a: unknown, b: unknown) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { numeric: true, sensitivity: 'base' });

const tempo = (d: Date | null) => (d ? d.getTime() : null);

/** Datas vazias ficam sempre no fim, em qualquer direção. */
const compararDatas = (a: Date | null, b: Date | null, dir: number) => {
  const ta = tempo(a);
  const tb = tempo(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return (ta - tb) * dir;
};

/**
 * Prioridade = a mesma ordem de trabalho dos cards: pendentes antes de concluídas;
 * entre pendentes, crítico → atenção/sem histórico → no prazo; empate pela data
 * limite mais próxima.
 */
const compararPrioridade = (a: CobrancaJanela, b: CobrancaJanela) => {
  const pa = ehPendente(a) ? 0 : 1;
  const pb = ehPendente(b) ? 0 : 1;
  if (pa !== pb) return pa - pb;
  const ua = PESO_URGENCIA[urgenciaDaCobranca(a) ?? 'normal'];
  const ub = PESO_URGENCIA[urgenciaDaCobranca(b) ?? 'normal'];
  if (ua !== ub) return ua - ub;
  return compararDatas(dataDoCampo(a, 'limite'), dataDoCampo(b, 'limite'), 1);
};

export const ordenarCobrancas = <T extends CobrancaJanela>(itens: T[], ordem: Ordem): T[] => {
  const dir = ordem.direcao === 'asc' ? 1 : -1;
  const cmp = (a: T, b: T): number => {
    switch (ordem.campo) {
      case 'prioridade': return compararPrioridade(a, b) * dir;
      case 'uc': return compararTexto(a.uc, b.uc) * dir;
      case 'cliente': return compararTexto(a.nome, b.nome) * dir;
      case 'mes': return (numeroMesReferencia(a.expectedMonth) - numeroMesReferencia(b.expectedMonth)) * dir;
      case 'responsavel': return compararTexto(a.responsavel_nome, b.responsavel_nome) * dir;
      case 'situacao':
        return ((ORDEM_SITUACAO.get(situacaoDaCobranca(a)) ?? 0) - (ORDEM_SITUACAO.get(situacaoDaCobranca(b)) ?? 0)) * dir;
      default: return compararDatas(dataDoCampo(a, ordem.campo), dataDoCampo(b, ordem.campo), dir);
    }
  };
  // Desempate estável pela prioridade de trabalho.
  return [...itens].sort((a, b) => cmp(a, b) || compararPrioridade(a, b));
};

export const contarPorSituacao = (itens: CobrancaJanela[]): Record<SituacaoCobranca, number> => {
  const contagem = Object.fromEntries(SITUACOES.map((s) => [s.valor, 0])) as Record<SituacaoCobranca, number>;
  for (const item of itens) contagem[situacaoDaCobranca(item)]++;
  return contagem;
};
